/**
 * The words on a picker's cards: each option's name, the promise it makes,
 * and a quieter line of numbers. A picker's cards carry their own
 * explanation, so nothing here needs a "what's this?" beside it.
 *
 * The numbers are read from the constants the game itself uses
 * (`@interlock/modes`, `@interlock/protocol`), never retyped, for the same
 * reason `copy/explanations.ts` reads them: a rebalance must not be able to
 * leave a card saying something the game no longer does.
 */
import { ticksToMs } from '@interlock/engine';
import {
  HARDNESS_GRAVITY_PERMILLE,
  HARDNESS_IDS,
  describeHardness,
  survivalFloorTicks,
} from '@interlock/modes';
import type { Hardness } from '@interlock/modes';
import { AI_LEVELS, aiLevelName, duelVariantName, duelVariantTagline } from '@interlock/protocol';
import type { AiLevel, RoomVariant } from '@interlock/protocol';

import type { RadioCardOption } from '@artibens/app-shell';
import { explainAiLevel, explainVariant } from './explanations.js';

const HARDNESS_PROMISE: Readonly<Record<Hardness, string>> = {
  easy: 'Pieces fall slowly. Time to think.',
  normal: 'The standard pace.',
  hard: 'Faster from the first piece.',
  expert: 'As fast as it gets.',
};

function gravityLine(hardness: Hardness): string {
  return `Gravity ×${(HARDNESS_GRAVITY_PERMILLE[hardness] / 1000).toFixed(1)}`;
}

function floorLine(hardness: Hardness): string {
  const seconds = ticksToMs(survivalFloorTicks(hardness)) / 1000;
  return `the floor rises every ${String(Math.round(seconds))}s`;
}

/**
 * The four hardness cards. `risingFloor` adds how often the floor rises, for
 * the modes and variants that have one.
 */
export function hardnessCards(risingFloor = false): readonly RadioCardOption<Hardness>[] {
  return HARDNESS_IDS.map((hardness, index) => ({
    value: hardness,
    name: describeHardness(hardness),
    tagline: HARDNESS_PROMISE[hardness],
    detail: risingFloor
      ? `${gravityLine(hardness)} · ${floorLine(hardness)}`
      : gravityLine(hardness),
    meter: index + 1,
  }));
}

/** The first sentence of a paragraph: what a card has room for. */
function firstSentence(text: string): string {
  const end = text.search(/[.!?](\s|$)/);
  return end === -1 ? text : text.slice(0, end + 1);
}

/** One card per variant: its name, its tagline, and the first sentence of its explanation. */
export function variantCards(
  variants: readonly RoomVariant[],
): readonly RadioCardOption<RoomVariant>[] {
  return variants.map((variant) => ({
    value: variant,
    name: duelVariantName(variant),
    tagline: duelVariantTagline(variant),
    detail: firstSentence(explainVariant(variant)),
  }));
}

/** The level's own explanation without the "Easy — " it opens on: the card's name already says it. */
function aiPromise(level: AiLevel): string {
  const text = explainAiLevel(level);
  const dash = text.indexOf('— ');
  const body = dash === -1 ? text : text.slice(dash + 2);
  return body.charAt(0).toUpperCase() + body.slice(1);
}

/** The four levels of the computer, each with what it plays like. */
export function aiLevelCards(): readonly RadioCardOption<AiLevel>[] {
  return AI_LEVELS.map((level, index) => {
    const promise = aiPromise(level);
    const tagline = firstSentence(promise);
    const rest = promise.slice(tagline.length).trim();
    return {
      value: level,
      name: aiLevelName(level),
      tagline,
      detail: rest === '' ? undefined : rest,
      meter: index + 1,
    };
  });
}
